import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar, BookOpen, CheckCircle2, Sparkles } from "lucide-react";
import MyCourseAccess from "@/components/MyCourseAccess";
import RebookModal from "@/components/reiki/RebookModal";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [reikiBookings, setReikiBookings] = useState([]);
  const [rebookFor, setRebookFor] = useState(null);

  const loadReiki = async () => {
    try {
      const { data } = await api.get("/reiki/bookings/me");
      setReikiBookings(Array.isArray(data) ? data : []);
    } catch {
      setReikiBookings([]);
    }
  };

  useEffect(() => {
    if (user === false) { nav("/login", { state: { from: "/dashboard" } }); return; }
    if (!user) return;
    (async () => {
      const { data: bs } = await api.get("/bookings/me");
      setBookings(bs);
      // Build enrollment summary from paid course bookings
      const courseBookings = bs.filter((b) => b.offering_type === "course" && b.status === "paid");
      const enrolled = await Promise.all(courseBookings.map(async (b) => {
        try {
          // Need slug to fetch lessons; fetch the offering by id via /offerings list lookup
          // Simpler: use bookings_with_slug enrichment via single fetch
          return b;
        } catch { return null; }
      }));
      setEnrollments(enrolled.filter(Boolean));
      await loadReiki();
    })();
  }, [user, nav]);

  if (!user || user === false) return <div className="pt-40 container-aviana text-aviana-muted">Loading…</div>;

  const courseBookings = bookings.filter((b) => b.offering_type === "course" && b.status === "paid");
  const eventBookings = bookings.filter((b) => b.offering_type !== "course");

  return (
    <div className="pt-32 pb-24" data-testid="dashboard-page">
      <div className="container-aviana">
        <p className="overline mb-3">Your sanctuary</p>
        <h1 className="font-serif text-5xl md:text-6xl text-aviana-ink">Hello, {user.name?.split(" ")[0] || "sister"}.</h1>

        <div className="mt-16">
          <MyCourseAccess />
        </div>

        <div className="mt-4 grid lg:grid-cols-2 gap-10">
          <section data-testid="my-courses">
            <h2 className="font-serif text-3xl text-aviana-ink mb-6 flex items-center gap-3"><BookOpen size={22} className="text-aviana-gold" /> My courses</h2>
            {courseBookings.length === 0 ? (
              <div className="card-aviana p-8">
                <p className="text-aviana-muted">No courses yet — your library awaits.</p>
                <Link to="/courses" className="btn-secondary mt-5 inline-flex">Browse courses</Link>
              </div>
            ) : (
              <div className="space-y-4">
                {courseBookings.map((b) => (
                  <CourseRow key={b.id} booking={b} />
                ))}
              </div>
            )}
          </section>
          <section data-testid="my-bookings">
            <h2 className="font-serif text-3xl text-aviana-ink mb-6 flex items-center gap-3"><Calendar size={22} className="text-aviana-gold" /> Upcoming bookings</h2>
            {eventBookings.length === 0 ? (
              <div className="card-aviana p-8">
                <p className="text-aviana-muted">Nothing booked yet. Find your next retreat or training.</p>
                <Link to="/retreats" className="btn-secondary mt-5 inline-flex">Explore retreats</Link>
              </div>
            ) : (
              <div className="space-y-4">
                {eventBookings.map((b) => (
                  <div key={b.id} className="card-aviana p-6 flex items-center gap-5">
                    <div className="w-12 h-12 rounded-full bg-aviana-surface flex items-center justify-center">
                      <CheckCircle2 size={22} className="text-aviana-gold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="overline">{b.offering_type}</p>
                      <h4 className="font-serif text-xl text-aviana-ink truncate">{b.offering_title}</h4>
                      <p className="text-xs text-aviana-muted">
                        Paid ${b.amount_paid_usd.toLocaleString()} of ${b.total_due_usd.toLocaleString()}{b.payment_type === "deposit" ? " · Balance due" : ""}
                      </p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full ${b.status === "paid" ? "bg-aviana-surface text-aviana-ink" : "bg-aviana-gold/30 text-aviana-ink"}`}>
                      {b.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {reikiBookings.length > 0 && (
          <section className="mt-16" data-testid="my-reiki-bookings">
            <h2 className="font-serif text-3xl text-aviana-ink mb-6 flex items-center gap-3">
              <Sparkles size={22} className="text-aviana-gold" /> Your reiki sessions
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {reikiBookings.map((b) => (
                <ReikiBookingRow
                  key={b.id}
                  booking={b}
                  onRebook={() => setRebookFor(b)}
                />
              ))}
            </div>
          </section>
        )}

      </div>

      {rebookFor && (
        <RebookModal
          source={rebookFor}
          endpoint={`/reiki/bookings/${rebookFor.id}/rebook`}
          onClose={() => setRebookFor(null)}
          onSaved={async () => { setRebookFor(null); await loadReiki(); }}
        />
      )}
    </div>
  );
}

function ReikiBookingRow({ booking, onRebook }) {
  const start = new Date(booking.start_iso);
  const statusLabel = booking.status?.replace(/_/g, " ") || "";
  const hasCardOnFile = !!booking.hold_payment_method_id;
  return (
    <div className="card-aviana p-6" data-testid={`reiki-booking-${booking.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="overline">{booking.tier_name}</p>
          <h4 className="font-serif text-xl text-aviana-ink mt-1">
            {start.toLocaleString([], { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </h4>
          <p className="mt-1 text-xs text-aviana-muted">
            {booking.duration_min} min · {booking.attendee_count} attendee{booking.attendee_count === 1 ? "" : "s"}
            {booking.is_rebook && " · Rebooked follow-up"}
          </p>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${booking.status === "confirmed" ? "bg-aviana-sage/20 text-aviana-sage" : booking.status === "cancelled" ? "bg-red-100 text-red-700" : "bg-aviana-surface text-aviana-ink"}`}>
          {statusLabel}
        </span>
      </div>
      {hasCardOnFile && booking.status !== "cancelled" && (
        <button
          onClick={onRebook}
          className="mt-4 text-xs uppercase tracking-[0.18em] text-aviana-goldDeep hover:text-aviana-plum inline-flex items-center gap-1.5"
          data-testid={`reiki-book-another-${booking.id}`}
        >
          <Sparkles size={12} /> Book another session
        </button>
      )}
    </div>
  );
}

function CourseRow({ booking }) {
  // We only have title and offering_id from booking; we need slug. We hit /offerings and find by id.
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  useEffect(() => {
    (async () => {
      const { data: courses } = await api.get("/offerings?type=course");
      const c = courses.find((x) => x.id === booking.offering_id);
      if (!c) return;
      setCourse(c);
      try {
        const { data } = await api.get(`/courses/${c.slug}/lessons`);
        setProgress({ total: data.course.lessons.length, done: data.completed_lessons.length });
      } catch {}
    })();
  }, [booking.offering_id]);
  if (!course) return null;
  const pct = progress ? Math.round((progress.done / Math.max(progress.total, 1)) * 100) : 0;
  return (
    <Link to={`/courses/${course.slug}/learn`} className="card-aviana p-6 flex items-center gap-5" data-testid={`course-row-${course.slug}`}>
      <img src={course.image_url} alt="" className="w-20 h-20 rounded-2xl object-cover" />
      <div className="flex-1 min-w-0">
        <h4 className="font-serif text-xl text-aviana-ink truncate">{course.title}</h4>
        <div className="mt-2 h-1.5 bg-aviana-border rounded-full overflow-hidden">
          <div className="h-full bg-aviana-gold" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-aviana-sage mt-1">{progress?.done || 0}/{progress?.total || 0} lessons · {pct}%</p>
      </div>
      <span className="text-aviana-ink">→</span>
    </Link>
  );
}
